import { useState } from 'react';
import dayjs from 'dayjs';
import { Calendar as CalendarIcon } from 'lucide-react';
import { TabProps, Tabs, TabsContent, TabsList, TabsTrigger } from '../../../../../../components/Common/UI/Tabs';
import { Button } from '../../../../../../components/Common/UI/Button';
import ModalBatchDownload from './Modals/BatchDownload';
import { cn } from '../../../../../../utils/styles';
import AccountChecks from './Checks';
import DataTable from '../../../../../../components/Common/UI/DataTable';
import { accountChecksCol } from './Checks/columns';
import DataPicker from '../../../../../../components/Common/UI/DataPicker';
import AccountDetailStatements from './Statements';

export interface IDates {
  from: Date;
  to: Date;
}

const now = dayjs();

const defaultValues: IDates = {
  from: now.subtract(2, 'year').toDate(),
  to: now.toDate(),
};

function AccountDetailNoResult({ placeholder }: { placeholder: string }) {
  return <div className="text-primary font-medium text-center">{placeholder}</div>;
}

function AccountDetailContent() {
  const [state, setState] = useState<IDates>(defaultValues);
  const [showResult, setShowResult] = useState<boolean>(true);

  const handleOnChange = (key: keyof IDates, payload?: Date) => {
    if (payload) {
      setState({ ...state, [key]: payload });
    }
  };

  const handleSearch = () => {
    if (
      !dayjs(state.from).isSame(dayjs(defaultValues.from), 'day') ||
      !dayjs(state.to).isSame(dayjs(defaultValues.to).toDate(), 'day')
    ) {
      setShowResult(false);
    } else {
      setShowResult(true);
    }
  };

  const tabs: TabProps[] = [
    {
      value: 'statements',
      label: 'Statements',
      content: showResult ? (
        <AccountDetailStatements />
      ) : (
        <AccountDetailNoResult placeholder="No Document for Checking Statement" />
      ),
    },
    {
      value: 'visa',
      label: 'Visa',
      content: showResult ? <AccountDetailStatements /> : <AccountDetailNoResult placeholder="No Document for Visa" />,
    },
    {
      value: 'eTax',
      label: 'Taxes',
      content: showResult ? <AccountDetailStatements /> : <AccountDetailNoResult placeholder="No Document for Taxes" />,
    },
    {
      value: 'notifications',
      label: 'Notices',
      content: showResult ? (
        <AccountDetailStatements />
      ) : (
        <AccountDetailNoResult placeholder="No Document for Notices" />
      ),
    },
    {
      value: 'mortgageStatements',
      label: 'Mortgage Statements',
      content: showResult ? (
        <AccountDetailStatements />
      ) : (
        <AccountDetailNoResult placeholder="No Document for Mortgage Statements" />
      ),
    },
    {
      value: 'checks',
      label: 'Checks',
      content: <AccountChecks />,
    },
  ];

  const [isCheck, setIsCheck] = useState<boolean>(false);

  const handleChange = (payload: string) => {
    setIsCheck(payload === 'checks');
  };

  return (
    <div>
      <Tabs
        defaultValue="statements"
        onClick={e => {
          const target = e.target as HTMLElement;

          if (target?.innerText) {
            window.location.hash = target.innerText.toLowerCase();
          }
        }}
      >
        <div className="flex flex-wrap w-full border rounded-lg page-heading p-[20px] bg-white">
          <div
            className={cn('flex flex-wrap items-center w-full gap-[20px] justify-between mb-[20px]', {
              'justify-end': isCheck,
            })}
          >
            {!isCheck ? (
              <div className="flex items-center gap-[20px]">
                <DataPicker
                  date={state.from}
                  icon={
                    <CalendarIcon
                      className={cn(
                        'ml-[15px] h-[18px] w-[18px] opacity-70 [&_rect]:stroke-addition5 ' +
                          '[&_path]:stroke-addition5',
                      )}
                    />
                  }
                  onSelect={e => handleOnChange('from', e)}
                />
                <DataPicker
                  date={state.to}
                  icon={
                    <CalendarIcon
                      className={cn(
                        'ml-[15px] h-[18px] w-[18px] opacity-70 [&_rect]:stroke-addition5 ' +
                          '[&_path]:stroke-addition5',
                      )}
                    />
                  }
                  onSelect={e => handleOnChange('to', e)}
                />
                <Button onClick={handleSearch}>Search</Button>
              </div>
            ) : null}
            <ModalBatchDownload />
          </div>
          <TabsList className="h-[44px]">
            {tabs.map(({ value, label }) => (
              <TabsTrigger
                className="px-[10px] xl:px-[30px]"
                key={`${value}-trigger`}
                value={value}
                onClick={() => handleChange(value)}
              >
                {label}
              </TabsTrigger>
            ))}
          </TabsList>
        </div>

        {tabs.map(({ value, content }) => (
          <TabsContent key={value} value={value} className="bg-white rounded-lg border p-[20px]">
            {content}
          </TabsContent>
        ))}
      </Tabs>
      {isCheck ? (
        <div className="bg-white rounded-lg border p-[20px] mt-2">
          <DataTable
            columns={accountChecksCol}
            data={[]}
            emptyText="No Search Result"
            initialState={{ pagination: { pageSize: 10 } }}
          />
        </div>
      ) : null}
    </div>
  );
}

export default AccountDetailContent;
